import Reading from '@assets/svg/reading.svg'
import { Button, Callout, Environment, InfluencerTransaction, User, onEnvironmentChange, onUserChange } from '@hub/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SubscriptionLike } from 'rxjs'
import TransferReceiptComponent from '../transfer-receipt/transfer-receipt.component'
import './card-payment-data.component.scoped.scss'

interface Props extends React.HTMLAttributes<HTMLDivElement> {
  canEdit: boolean
  hideData: boolean
  hideQipu?: boolean
  transactionObject?: InfluencerTransaction | null
  seeData: (event: any) => any
}

export default ({ canEdit, hideData, hideQipu, transactionObject = null, seeData }: Props) => {
  const [t] = useTranslation('cardPaymentData')
  const onEnvironmentChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const onUserChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [user, setUser] = useState<User | null>(null)
  const [urlCreators, setUrlCreators] = useState<string>('')

  let frozenData = null
  if (canEdit) {
    frozenData = (
      <Callout className="mt-0 mb-5" title={t('warning.title')} icon="fa-regular fa-calendar-circle-exclamation">
        <div className="display-grid" style={{ gap: '0.5rem' }}>
          <span dangerouslySetInnerHTML={{ __html: t('warning.message') }}></span>
          <span dangerouslySetInnerHTML={{ __html: t('warning.complement') }}></span>
        </div>
      </Callout>
    )
  }

  useEffect(() => {
    onUserChangeSubscription.current = onUserChange.subscribe(async (user: User) => {
      if (user?.squidId) {
        setUser(user)
      }
    })

    return () => {
      onUserChangeSubscription?.current?.unsubscribe()
    }
  }, [])

  useEffect(() => {
    onEnvironmentChangeSubscription.current = onEnvironmentChange.subscribe((environments: Environment) => {
      setUrlCreators(environments?.squidProducts?.creators)
    })
    return () => onEnvironmentChangeSubscription?.current?.unsubscribe()
  }, [])

  const hideDataCheck = useCallback(
    (data?: string) => {
      if (hideData) {
        return (
          <>
            {Array.from(Array(data?.length).keys()).map(() => {
              return '•'
            })}
          </>
        )
      }
      return data
    },
    [hideData]
  )

  return (
    <div className="card-payment-data">
      {frozenData}
      <section className="payment-card">
        <div className="bank-data" role="button" tabIndex={0} onClick={() => seeData(true)}>
          <div className="bank-icon">
            <i className="fa-solid fa-building-columns" style={{ fontSize: 54, marginRight: '1rem', color: 'var(--gray_light)' }}></i>
            <div className="icon-circle">
              <i
                className="fa-solid fa-circle-check"
                style={{
                  fontSize: 20,
                  background: 'var(--gray_light)',
                  color: 'var(--blue_dark)',
                  borderRadius: '50%',
                  boxShadow: 'var(--box_shadow)',
                }}
              ></i>
            </div>
          </div>
          <div>
            <p style={{ fontWeight: 600 }}>{hideDataCheck(user?.banks?.[0]?.holderName)}</p>
            <p className="bank-infos mb-1">
              Ag {hideDataCheck(user?.banks?.[0]?.bankAccountAgency)} {user?.banks?.[0]?.bankAccountType === 'checking' ? 'C/C' : 'C/P'}{' '}
              {hideDataCheck(
                user?.banks?.[0]?.bankAccountNumber + (user?.banks?.[0]?.bankAccountDigit ? '-' + user?.banks?.[0]?.bankAccountDigit : '')
              )}
            </p>
            <p className="bank-infos mb-1">{user?.banks?.[0]?.bankName}</p>
            <div className="display-flex">
              <p className="payment-type">{user?.banks?.[0]?.paymentType?.toUpperCase()}</p>
            </div>
          </div>
        </div>
        <TransferReceiptComponent transactionObject={transactionObject} />
      </section>
      {user?.banks?.[0]?.paymentType === 'rpa' && (
        <section className="qipu-card">
          <header>
            <div className="reading">
              <img src={Reading} alt="reading" title="reading" className="img-fluid" />
            </div>
            <h5 style={{ fontWeight: 600 }}>{t('rpa.title')}</h5>
          </header>
          <p className="mb-3" style={{ marginLeft: '0.5rem', textAlign: 'center' }} dangerouslySetInnerHTML={{ __html: t('rpa.text') }} />
          <a href={`${urlCreators}/docs/o-que-e-mais-vantajoso-cnpj-ou-rpa?highlight=cnppj`} target="_blank">
            <Button size="sm" color="var(--text_color)" textColor="var(--background)" borderStyle="none">
              {t('knowMore')}
            </Button>
          </a>
        </section>
      )}
    </div>
  )
}
